import { ApplicationElement, html } from '@effectivastudio/lit-components';
import { ifDefined } from 'lit/directives/if-defined.js';
import translations from './translations.js';
import style from './style.lit.css';

import '@effectivastudio/lit-components/components/icon/element.js';
import '@effectivastudio/lit-components/components/avatar/element.js';
import '@effectivastudio/lit-components/components/online_status/element.js';
import '@effectivastudio/lit-components/components/label/element.js';
import CommonActions from '@effectivastudio/lit-components/components/common_actions/element.js';

/**
 * Open invitation list item
 *
 * @prop userId - ID of the existing member
 * @prop userPicture - Link to users profile picture
 * @prop userName - Full name of existing member
 * @prop userUrl - Existing member's profile URL
 * @prop recipientEmail - Email of new member
 * @prop groupTitle - The invitation group's title
 * @prop groupUrl - The invitation group's profile URL
 * @prop createdAt - Invitation creation date
 * @prop cancelUrl - URL for invitation's deletion, will show confirmation
 */

export default class OpenInvitation extends ApplicationElement {
  /** @internal */
  static translations = translations;
  static styles = [ApplicationElement.styles, style];

  static properties = {
    userId: { type: Number },
    userPicture: { type: String },
    userName: { type: String },
    userUrl: { type: String },
    recipientEmail: { type: String },
    groupTitle: { type: String },
    groupUrl: { type: String },
    createdAt: { type: Date },
    cancelUrl: { type: String },
  };

  constructor() {
    super();
    this.commonActions = new CommonActions(this);
  }

  /** @internal */
  get isExistingMember() {
    return !!this.userName;
  }

  /** @internal */
  get isGroup() {
    return !!(this.groupTitle && this.groupUrl);
  }

  /** @internal */
  get date() {
    if (!this.createdAt) return;

    const wasCreatedThisYear =
      this.createdAt.getFullYear() === new Date().getFullYear();
    const dateFormat = wasCreatedThisYear ? '%d %b %H:%M' : '%d %b %Y';
    return this.i18n.strftime(this.createdAt, dateFormat);
  }

  async cancel() {
    if (!this.commonActions.confirmDialog(this.t('confirm_dialog_msg.default')))
      return false;

    try {
      await this.commonActions.delete(this.cancelUrl);
      this.commonActions.displaySnackBarSuccess(this.t('snack.cancel_success'));
      this.commonActions.visit(window.location.href);
    } catch (e) {
      this.commonActions.displaySnackBarError(this.t('snack.generic_error'));
    }
  }

  renderExistingMemberHeading() {
    return html`
      ${this.userUrl
        ? html`<a href=${this.userUrl}>${this.userName}</a>`
        : html`<span>${this.userName}</span>`}
      ${this.userId
        ? html`<ef-online-status
            uid=${this.userId}
            label="false"></ef-online-status>`
        : null}
    `;
  }

  renderGroup() {
    return html`
      <a href=${this.groupUrl}>
        <ef-label>${this.groupTitle}</ef-label>
      </a>
    `;
  }

  render() {
    return html`
      <main>
        <ef-avatar
          picture=${ifDefined(this.userPicture)}
          name=${ifDefined(this.userName)}>
        </ef-avatar>
        <div>
          <h4>
            ${this.isExistingMember
              ? this.renderExistingMemberHeading()
              : this.recipientEmail}
          </h4>
          ${this.isGroup ? this.renderGroup() : null}
          <time datetime=${this.createdAt}>${this.date}</time>
        </div>
        <button @click="${this.cancel}">
          <span>${this.t('actions.cancel')}</span>
          <ef-icon name="close"></ef-icon>
        </button>
      </main>
    `;
  }
}

OpenInvitation.registerTagName('bu-open-invitation');

customElements.get('bu-open-invitation') ||
  customElements.define('bu-open-invitation', OpenInvitation);
